import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "redux/store";
import { api } from "api/axiosInterceptor";
import { apiUrl } from "api/apiUrl";
import {
  decrementRequests,
  incrementRequests,
  setIsLoading,
  setShowToast,
} from "./application";
import {
  IFile,
  IFileResponse,
  IFileUploadData,
  IIntakeState,
  ISolutionAndObjective,
  IntakeObjectivesPayload,
} from "../../../types/overview";

function getFileNameFromContentDisposition(response: any): string {
  const disposition = response.headers["content-disposition"];
  const contentType = response.headers["content-type"];
  let filename = "download";

  if (disposition) {
    try {
      const starMatch = disposition.match(/filename\*\s*=\s*([^;]+)/i);
      if (starMatch && starMatch[1]) {
        const parts = starMatch[1].split("''");
        if (parts.length === 2) {
          filename = decodeURIComponent(parts[1]);
        }
      } else {
        const nameMatch = disposition.match(/filename\s*=\s*["']?([^"';]+)["']?/i);
        if (nameMatch && nameMatch[1]) {
          filename = nameMatch[1];
        }
      }
    } catch (err) {
      console.warn("Error extracting filename from Content-Disposition:", err);
    }
  }

  if (!/\.[a-z0-9]+$/i.test(filename)) {
    const extMap: Record<string, string> = {
      "application/pdf": ".pdf",
      "application/msword": ".doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
      "application/vnd.ms-excel": ".xls",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    };
    const ext = extMap[contentType];
    if (ext) {
      filename += ext;
    }
  }

  return filename;
}

const initialState: IIntakeState = {
  solutionAndObjective: {
    solution: "",
    objectives: [],
  },
  listOfFiles: {
    files: [],
    status: "",
  },
  uploadFileResult: [],
};

const slice = createSlice({
  name: "overview",
  initialState,
  reducers: {
    getSolution(state, action: PayloadAction<ISolutionAndObjective>) {
      state.solutionAndObjective = action.payload;
    },
    getListOfFiles(state, action: PayloadAction<IFileResponse>) {
      state.listOfFiles = action.payload;
    },
    uploadFileResponse(state, action: PayloadAction<IFile[]>) {
      state.uploadFileResult = action.payload;
    },
    resetUploadedFile(state) {
      state.listOfFiles = initialState.listOfFiles;
    },
    removeFileFromList(state, action: PayloadAction<string>) {
      if (state.listOfFiles.files) {
        state.listOfFiles.files = state.listOfFiles.files.filter(
          (file) => file.file_id !== action.payload
        );
      }
    },
  },
});

export const {
  getSolution,
  getListOfFiles,
  uploadFileResponse,
  resetUploadedFile,
  removeFileFromList,
} = slice.actions;

export default slice.reducer;

export function getFileList(file_type: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `files/${file_type}/`;
      const response = await api.get(path);
      dispatch(getListOfFiles(response.data));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function uploadFile(file_type: string, files: File[]) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `${apiUrl.upload_file}${file_type}/`;
      const formData = new FormData();

      files.forEach((file) => {
        formData.append(`files`, file);
      });

      const response = await api.post<IFileUploadData>(path, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        dispatch(getFileList(file_type));
        return { data: response.data, status: response.status };
      } else {
        throw new Error("Upload failed.");
      }
    } catch (error) {
      if (error instanceof Error) {
        dispatch(
          setShowToast({
            show: true,
            type: "error",
            toastMessage: error.message,
          })
        );
        dispatch(getFileList(file_type));
      }
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function uploadRfpFile(file_type: string, files: File[]) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/file-uploads/requirements/${file_type}/`;
      const formData = new FormData();
      const filesToUpload: File[] = [];
      const existingFileIds: (string | number)[] = [];

      files.forEach((file : any) => {
        if (file?.file_id) {
          existingFileIds.push(file.file_id);
        } else {
          filesToUpload.push(file);
        }
      });

      // Append the files to FormData
      filesToUpload.forEach((file) => {
        if (file.name.toLowerCase().endsWith(".zip")) {
          formData.append("zip_file", file);
        } else {
          formData.append("files", file);
        }
      });

      // Append the existing file IDs as a comma-separated string
      formData.append(`file_ids`, existingFileIds.join(","));

      const response : any = await api.post<IFile[]>(path, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if(response.data?.is_file_valid === false) {
        dispatch(setShowToast({
          show: true,
          type: "error",
          toastMessage: response?.data?.message || "File is not valid.",
          toastDuration: 8000,
          
        }));
      }
      dispatch(uploadFileResponse(response.data));
      return { data: response.data, status: response.status };
    } catch (error) {
      if (error instanceof Error) {
        dispatch(
          setShowToast({
            show: true,
            type: "error",
            toastMessage: error.message,
          })
        );
      }
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function getSolutionAndObjective() {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = apiUrl?.solutionAndObjective;
      const response = await api.get(path);
      dispatch(getSolution(response.data));
    } catch (error: any) {
      const status = error.response ? error.response.status : 500;
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
      return status;
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function deleteFile(file_id: string, file_type: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const path = `/files/delete/${file_id}/`;
      const resposne = await api.delete(path);
      if (resposne.status === 200 || resposne.status === 201) {
        if (file_type === "RFP_Response" || file_type === "RFP") {
          dispatch(getFileList(file_type));
        }
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "File deleted successfully!",
          })
        );
      }
      return resposne.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    }
  };
}

export function downloadUploadedFile(file_id: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/files/download/${file_id}/`;
      const response = await api.get(path, {responseType: "blob"});
      if (response.status === 200 || response.status === 201) {
        const contentType = response.headers["content-type"];
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          getFileNameFromContentDisposition(response)
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "File downloaded successfully!",
          })
        );
      }
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function setIntakeObjectives(data: IntakeObjectivesPayload) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = apiUrl?.updateSolutionObjectives;
      const response = await api.post(path, data);
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function saveAndExit(stage: number, postData: Record<string, any>) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    dispatch(incrementRequests());
    try {
      const formData = new FormData();
      if (stage === 6) {
        postData.forEach((file: File) => {
          formData.append(`files`, file);
          // formData.append(`file_ids`, "");
        });
        formData.append("data", "");
      } else {
        formData.append("data", JSON.stringify(postData));
      }
      const path = `${apiUrl.saveExit}/${stage}`;
      const { status } = await api.post(path, formData);
      return status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong while saving data!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
      dispatch(decrementRequests());
    }
  };
}
